import { createContext, use, useEffect } from "react";
import { useCurrentUser } from "@/hooks";
import {
  useCurrentOrganization,
  useCurrentEntityDetail,
  useMerchantActivePool,
  useEURMBalance,
  usePoolChainState,
  useTotalSupply,
} from "@/hooks/queries";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { UserType, OrganizationType } from "@/types";
import { AssetType } from "@/constants/enums";
import { hasEntityPublicKey } from "@/utils/helpers";
import { isoToEpochMs } from "@/utils/dateUtils";

export type BlockchainData = {
  initialized: boolean;
  loading: boolean;
  hasPublicKey: boolean;
  chainStateReady: boolean;
  chainStateError: boolean;
  balance: number;
  merchant: {
    currentlyOwedInterestAmount: number | null;
    nextOwedInterestAmount: number | null;
    nextPaymentDate: number;
    interestPaymentDue: boolean;
    lastPaymentDate: number;
  };
  inventoryProvider: {
    criticalInventoryValue: number | null;
    isEndPaymentDue: boolean | null;
    lastInventoryValue: number | null;
  };
  receivablesProvider: {
    residualCash: number | null;
  };
  admin: {
    totalSupply: number;
  };
};

const emptyBlockchainData: BlockchainData = {
  initialized: false,
  loading: true,
  hasPublicKey: false,
  chainStateReady: false,
  chainStateError: false,
  balance: 0,
  merchant: {
    currentlyOwedInterestAmount: null,
    nextOwedInterestAmount: null,
    nextPaymentDate: 0,
    interestPaymentDue: false,
    lastPaymentDate: 0,
  },
  inventoryProvider: {
    criticalInventoryValue: null,
    isEndPaymentDue: null,
    lastInventoryValue: null,
  },
  receivablesProvider: {
    residualCash: null,
  },
  admin: {
    totalSupply: 0,
  },
};

const BlockchainContext = createContext<BlockchainData>(emptyBlockchainData);

export function BlockchainProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation(["toasts"]);
  const { data: currentUser } = useCurrentUser();
  const { data: organization } = useCurrentOrganization();
  const { data: entity, isLoading: entityLoading } = useCurrentEntityDetail();

  const isMerchant = organization?.externalEntityType === OrganizationType.Merchant;
  const isFinancier = organization?.externalEntityType === OrganizationType.Financier;
  const isAdmin = currentUser?.userType === UserType.Developer;

  // Pool address is only available for merchant users via their active pool
  const { data: activePool } = useMerchantActivePool(
    isMerchant ? (organization?.externalEntityId ?? undefined) : undefined
  );
  const poolId = activePool?.id ?? null;
  const poolIsDeployed = !!activePool?.poolAddress;
  const chainStatePoolId = isMerchant && poolIsDeployed ? poolId : null;
  const chainStateQuery = usePoolChainState(chainStatePoolId);
  const poolKind = activePool?.assetType === AssetType.Receivable
    ? "receivable"
    : activePool?.assetType === AssetType.Inventory
      ? "inventory"
      : null;

  const balanceQuery = useEURMBalance(entity?.publicKey);
  const totalSupplyQuery = useTotalSupply(isAdmin);

  useEffect(() => {
    if (balanceQuery.isError) {
      toast.error(t("balanceLoadError"), { toastId: "balanceError" });
    }
  }, [balanceQuery.isError, t]);

  const nextPaymentDateIso = chainStateQuery.data?.nextPaymentDate;
  const refetchChainState = chainStateQuery.refetch;

  // refetch chain state when next payment date is due to triggere redirection
  useEffect(() => {
    const dueAt = isoToEpochMs(nextPaymentDateIso);
    if (!chainStatePoolId || !dueAt) return;
    const delay = dueAt - Date.now();
    if (delay <= 0 || delay > 24 * 60 * 60 * 1000) return;
    const timeoutId = window.setTimeout(() => {
      void refetchChainState();
    }, delay);
    return () => window.clearTimeout(timeoutId);
  }, [chainStatePoolId, nextPaymentDateIso, refetchChainState]);

  const needsEntity = isMerchant || isFinancier;
  // org must resolve before needsEntity is evaluated to avoid a false-ready flicker
  const orgResolved = !!currentUser && !!organization;
  const adminReady = !isAdmin || !totalSupplyQuery.isPending;
  const entityReady = !needsEntity || (!entityLoading && !!entity);
  const hasPublicKey = hasEntityPublicKey(entity?.publicKey);

  const data: BlockchainData = {
    initialized: orgResolved && adminReady && entityReady,
    hasPublicKey,
    loading:
      balanceQuery.isLoading ||
      chainStateQuery.isLoading ||
      totalSupplyQuery.isLoading,
    chainStateReady:
      !chainStatePoolId ||
      (!!chainStateQuery.data &&
        !chainStateQuery.isError &&
        !chainStateQuery.isFetching),
    chainStateError: !!chainStatePoolId && chainStateQuery.isError,
    balance: balanceQuery.data ?? 0,
    merchant: {
      interestPaymentDue: (chainStateQuery.data?.currentOwedInterest ?? 0) > 0,
      currentlyOwedInterestAmount: chainStateQuery.data?.currentOwedInterest ?? null,
      nextOwedInterestAmount: chainStateQuery.data?.nextOwedInterest ?? null,
      nextPaymentDate: isoToEpochMs(chainStateQuery.data?.nextPaymentDate),
      lastPaymentDate: isoToEpochMs(chainStateQuery.data?.lastPaymentDate),
    },
    inventoryProvider: {
      criticalInventoryValue:
        poolKind === "inventory" ? (chainStateQuery.data?.criticalInventoryValue ?? null) : null,
      isEndPaymentDue: chainStateQuery.data?.endPaymentDue ?? null,
      lastInventoryValue:
        poolKind === "inventory" ? (chainStateQuery.data?.lastInventoryValue ?? null) : null,
    },
    receivablesProvider: {
      residualCash:
        poolKind === "receivable" ? (chainStateQuery.data?.residualCash ?? null) : null,
    },
    admin: { totalSupply: totalSupplyQuery.data ?? 0 },
  };

  return (
    <BlockchainContext value={data}>
      {children}
    </BlockchainContext>
  );
}

export function useBlockchainData(): BlockchainData {
  return use(BlockchainContext);
}
